import { useState, useEffect } from 'react'
import { api } from '../services/api'
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  BoltIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function RagConfig() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [ragConfig, setRagConfig] = useState({
    chunkingStrategy: 'sentence_boundary',
    chunkSize: 512,
    chunkOverlap: 50,
    retrievalK: 5,
    similarityThreshold: 0.7,
    mmrLambda: 0.5,
    enableMmr: true,
    industryType: 'general',
    brandVoice: 'professional',
    cacheStrategy: '1hr'
  })

  useEffect(() => {
    fetchRagConfig()
  }, [])

  const fetchRagConfig = async () => {
    try {
      const response = await api.get('/admin/rag/config')
      if (response.data.success) {
        setRagConfig(response.data.config)
      }
    } catch (err) {
      console.error('Failed to fetch RAG config:', err)
    }
  }

  const saveRagConfig = async () => {
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await api.post('/admin/rag/config', ragConfig)

      if (response.data.success) {
        setSuccess('RAG configuration saved successfully!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError('Failed to save configuration')
      }
    } catch (err) {
      console.error('Failed to save RAG config:', err)
      if (err.response?.data?.detail) {
        setError(err.response.data.detail)
      } else {
        setError('Failed to save configuration. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const selectClasses = "w-full px-3.5 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">RAG Configuration</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage the Retrieval-Augmented Generation system settings for all users and tenants.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-sm">
          {success}
        </div>
      )}

      {/* Chunking Strategy */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <DocumentTextIcon className="h-5 w-5 text-slate-400" />
          Chunking Strategy
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Chunking Method
            </label>
            <select
              value={ragConfig.chunkingStrategy}
              onChange={(e) => setRagConfig({...ragConfig, chunkingStrategy: e.target.value})}
              className={selectClasses}
            >
              <option value="fixed_size">Fixed Size</option>
              <option value="sentence_boundary">Sentence Boundary</option>
              <option value="paragraph_aware">Paragraph Aware</option>
              <option value="sliding_window">Sliding Window</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Chunk Size (tokens)
            </label>
            <select
              value={ragConfig.chunkSize}
              onChange={(e) => setRagConfig({...ragConfig, chunkSize: parseInt(e.target.value)})}
              className={selectClasses}
            >
              <option value={256}>256 tokens</option>
              <option value={512}>512 tokens</option>
              <option value={1024}>1024 tokens</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Chunk Overlap (%)
            </label>
            <input
              type="range"
              min="0"
              max="50"
              value={ragConfig.chunkOverlap}
              onChange={(e) => setRagConfig({...ragConfig, chunkOverlap: parseInt(e.target.value)})}
              className="w-full accent-brand-600"
            />
            <div className="text-xs text-slate-500 mt-1">{ragConfig.chunkOverlap}%</div>
          </div>
        </div>
      </div>

      {/* Retrieval Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
          Retrieval Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Top-K Results
            </label>
            <select
              value={ragConfig.retrievalK}
              onChange={(e) => setRagConfig({...ragConfig, retrievalK: parseInt(e.target.value)})}
              className={selectClasses}
            >
              <option value={3}>3 results</option>
              <option value={5}>5 results</option>
              <option value={10}>10 results</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Similarity Threshold
            </label>
            <select
              value={ragConfig.similarityThreshold}
              onChange={(e) => setRagConfig({...ragConfig, similarityThreshold: parseFloat(e.target.value)})}
              className={selectClasses}
            >
              <option value={0.7}>0.7 (Relaxed)</option>
              <option value={0.8}>0.8 (Balanced)</option>
              <option value={0.9}>0.9 (Strict)</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              id="enable-mmr"
              type="checkbox"
              checked={ragConfig.enableMmr}
              onChange={(e) => setRagConfig({...ragConfig, enableMmr: e.target.checked})}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 rounded"
            />
            <label htmlFor="enable-mmr" className="ml-2 block text-sm text-slate-700">
              Enable MMR Diversification
            </label>
          </div>
          {ragConfig.enableMmr && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                MMR Lambda (Diversity)
              </label>
              <input
                type="range"
                min="0.1"
                max="0.9"
                step="0.1"
                value={ragConfig.mmrLambda}
                onChange={(e) => setRagConfig({...ragConfig, mmrLambda: parseFloat(e.target.value)})}
                className="w-full accent-brand-600"
              />
              <div className="text-xs text-slate-500 mt-1">{ragConfig.mmrLambda} (0.1=diverse, 0.9=relevant)</div>
            </div>
          )}
        </div>
      </div>

      {/* Industry & Brand Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <BuildingOfficeIcon className="h-5 w-5 text-slate-400" />
          Industry & Brand Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Industry Type
            </label>
            <select
              value={ragConfig.industryType}
              onChange={(e) => setRagConfig({...ragConfig, industryType: e.target.value})}
              className={selectClasses}
            >
              <option value="general">General</option>
              <option value="healthcare">Healthcare</option>
              <option value="finance">Finance</option>
              <option value="technology">Technology</option>
              <option value="education">Education</option>
              <option value="retail">Retail</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Brand Voice
            </label>
            <select
              value={ragConfig.brandVoice}
              onChange={(e) => setRagConfig({...ragConfig, brandVoice: e.target.value})}
              className={selectClasses}
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="casual">Casual</option>
              <option value="technical">Technical</option>
            </select>
          </div>
        </div>
      </div>

      {/* Caching Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <BoltIcon className="h-5 w-5 text-slate-400" />
          Caching Strategy
        </h3>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Cache TTL
          </label>
          <select
            value={ragConfig.cacheStrategy}
            onChange={(e) => setRagConfig({...ragConfig, cacheStrategy: e.target.value})}
            className={selectClasses}
          >
            <option value="5min">5 minutes</option>
            <option value="1hr">1 hour</option>
            <option value="24hr">24 hours</option>
          </select>
        </div>
      </div>

      {/* Save Configuration */}
      <div className="flex justify-end">
        <button
          onClick={saveRagConfig}
          className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 transition-colors disabled:opacity-50"
          disabled={loading}
        >
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <span>Save Configuration</span>
          )}
        </button>
      </div>
    </div>
  )
}
